/**
 * One dashboard, as the agency reads it: its pages as tabs, the period, live
 * numbers, and Edit to open the builder.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { DEFAULT_SETTINGS } from '../reports/BuilderBar.tsx';
import { Canvas } from '../reports/Canvas.tsx';
import { DateControl } from '../reports/DateControl.tsx';
import { useQueries } from '../reports/hooks.ts';
import { PageTabs } from '../reports/PageTabs.tsx';
import { ChangeFormatContext, changeFormatOf } from '../reports/change-format.ts';
import { ReportFactsContext } from '../reports/report-facts.ts';
import {
  reportsApi,
  type Catalogue,
  type Page,
  type RangeSetting,
  type ReportDocument,
} from '../reports/reports-api.ts';
import { ThemeScope } from '../reports/ThemeScope.tsx';
import { downloadPage } from '../reports/xlsx-downloads.ts';

const NO_OP = () => undefined;

export function DashboardView({
  organisationId,
  documentId,
  catalogue,
  client,
  onEdit,
  onDelete,
}: {
  organisationId: string;
  documentId: string;
  catalogue: Catalogue;
  client: { name: string; website: string | null };
  onEdit: (loaded: { document: ReportDocument; pages: Page[] }) => void;
  /** Removes the dashboard; it can be restored from Removed in the Explore panel. */
  onDelete: () => Promise<void>;
}): ReactNode {
  const [loaded, setLoaded] = useState<{ document: ReportDocument; pages: Page[] } | null>(null);
  const [index, setIndex] = useState(0);
  const [range, setRange] = useState<RangeSetting | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    setLoaded(null);
    setIndex(0);
    reportsApi
      .get(organisationId, documentId)
      .then((data) => {
        setLoaded(data);
        setRange(data.document.settings.range ?? DEFAULT_SETTINGS.range);
      })
      .catch(() => setLoaded(null));
  }, [organisationId, documentId]);

  const page = loaded?.pages[Math.min(index, (loaded?.pages.length ?? 1) - 1)];
  const widgets = page?.widgets ?? [];
  const keyOf = useCallback((at: number) => widgets[at]?.id ?? `w${at}`, [widgets]);
  const settings = loaded?.document.settings;
  const { results } = useQueries({
    organisationId,
    businessUnitId: loaded?.document.business_unit_id ?? null,
    mode: settings?.mode ?? 'LIVE',
    timezone: settings?.timezone ?? DEFAULT_SETTINGS.timezone,
    range: range ?? DEFAULT_SETTINGS.range,
    compare: settings?.compare ?? DEFAULT_SETTINGS.compare,
    widgets,
    keyOf,
  });

  if (!loaded || !page) return <p className="ag-loading">Loading…</p>;
  return (
    <div className="ag-dash">
      <header className="ag-dash-head">
        <h2>{loaded.document.name}</h2>
        <span className="ag-spacer" />
        <DateControl range={range ?? DEFAULT_SETTINGS.range} onRange={setRange} />
        <button
          type="button"
          className="btn small secondary"
          onClick={() =>
            downloadPage({
              widgets,
              results,
              keyOf,
              catalogue,
              fileParts: [client.name, loaded.document.name, page.title],
            }) || setProblem('This page has no numbers to download yet.')
          }
        >
          Download XLS
        </button>
        <button type="button" className="btn small" onClick={() => onEdit(loaded)}>
          Edit
        </button>
        <button
          type="button"
          className="btn small secondary"
          onClick={() =>
            onDelete().catch(() => setProblem('This dashboard could not be removed. Try again.'))
          }
        >
          Delete
        </button>
      </header>
      {problem && (
        <p className="rh-access-msg rh-access-error" role="alert">
          {problem}
        </p>
      )}
      {loaded.pages.length > 1 && (
        <PageTabs
          pages={loaded.pages}
          active={index}
          editable={false}
          onSelect={setIndex}
          onChange={NO_OP}
        />
      )}
      <ReportFactsContext.Provider
        value={{
          clientName: client.name,
          clientUrl: client.website,
          reportName: loaded.document.name,
          pageTitle: page.title,
        }}
      >
        <ChangeFormatContext.Provider value={changeFormatOf(settings?.compare)}>
          <ThemeScope theme={loaded.document.theme} layout="dashboard">
            <Canvas
              widgets={widgets}
              results={results}
              sources={catalogue.sources}
              editable={false}
              autoAlign={false}
              selected={null}
              onSelect={NO_OP}
              onChange={NO_OP}
              onRemove={NO_OP}
              keyOf={keyOf}
            />
          </ThemeScope>
        </ChangeFormatContext.Provider>
      </ReportFactsContext.Provider>
    </div>
  );
}
